'use client';
import { useComboBox } from '@react-aria/combobox';
import { useFilter } from '@react-aria/i18n';
import { useComboBoxState } from '@react-stately/combobox';
import React__default, { useCallback, useMemo } from 'react';
import { useListboxList, SwanListboxListContext } from '../listbox/listbox-list.context.js';
import { useModeExtractor } from '../../hooks/use-mode-extractor.js';
import { useCollectionContext } from '../../contexts/internal/collection/collection.context.js';
import { SwanPopoverContextProvider } from '../popover/popover.context.js';
import { useNonNullishContext } from '../../hooks/use-non-nullish-context.js';

const SwanComboboxContext = /*#__PURE__*/ React__default.createContext(undefined);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SwanComboboxContextProvider(props) {
    const { children, onSelectionChange, disableAutoFilter = false, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, inputValue, defaultInputValue, onInputChange, allowsCustomValue, allowEmptyCollection } = props;
    const { childItems, items, disabledKeys } = useCollectionContext();
    // react-aria introduced a typescript change in 3.9.1, so wrap our existing callback to match the new call signature
    const onChange = useCallback((key)=>{
        var _key_toString;
        const val = typeof key === 'number' ? key : (_key_toString = key === null || key === void 0 ? void 0 : key.toString()) !== null && _key_toString !== void 0 ? _key_toString : '';
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(val !== null && val !== void 0 ? val : '');
    }, [
        onSelectionChange
    ]);
    // Setup filter function and state.
    const { contains } = useFilter({
        sensitivity: 'base'
    });
    const filterFunc = disableAutoFilter ? undefined : contains;
    const state = useComboBoxState({
        children: childItems,
        items,
        defaultFilter: filterFunc,
        onSelectionChange: onChange,
        inputValue,
        defaultInputValue,
        onInputChange,
        allowsCustomValue,
        allowsEmptyCollection: allowEmptyCollection
    });
    // Setup refs and get props for child elements.
    const buttonRef = React__default.useRef(null);
    const inputRef = React__default.useRef(null);
    const triggerRef = React__default.useRef(null);
    const listBoxRef = React__default.useRef(null);
    const popoverRef = React__default.useRef(null);
    const { buttonProps, inputProps, listBoxProps, labelProps } = useComboBox({
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        menuTrigger: 'input',
        inputRef,
        buttonRef,
        listBoxRef,
        popoverRef,
        disabledKeys
    }, state);
    const modes = useModeExtractor(inputRef, state.isOpen);
    const popoverContextValue = useMemo(()=>({
            popoverRef,
            triggerRef: (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) ? triggerRef : inputRef,
            overlayState: state,
            fullWidth: true,
            modes
        }), [
        inputRef,
        popoverRef,
        triggerRef,
        state,
        modes
    ]);
    const contextValue = useMemo(()=>({
            buttonProps,
            inputProps,
            labelProps,
            buttonRef,
            inputRef,
            triggerRef,
            setInputValue: state.setInputValue
        }), [
        buttonProps,
        inputProps,
        labelProps,
        state
    ]);
    const listContext = useListboxList(listBoxProps, listBoxRef, state);
    return /*#__PURE__*/ React__default.createElement(SwanPopoverContextProvider, {
        value: popoverContextValue
    }, /*#__PURE__*/ React__default.createElement(SwanComboboxContext.Provider, {
        value: contextValue
    }, /*#__PURE__*/ React__default.createElement(SwanListboxListContext.Provider, {
        value: listContext
    }, children)));
}
function useSwanComboboxContext() {
    return useNonNullishContext(SwanComboboxContext);
}

export { SwanComboboxContext, SwanComboboxContextProvider, useSwanComboboxContext };
